import { useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";
import { stallAPI, aiAPI } from "../lib/api";
import { Zap, Brain, Shield, Star, Sparkles, Loader2, X, Trash } from "lucide-react";

const DECISIONS = [
  {
    key: "delete",
    label: "Delete",
    icon: Trash,
    color: "bg-red-50 border-red-300 text-red-800 hover:bg-red-100",
    activeColor: "bg-red-500 border-red-600 text-white",
    desc: "Not adding value. Should we stop doing this?",
  },
  {
    key: "automate",
    label: "Automate",
    icon: Zap,
    color: "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100",
    activeColor: "bg-amber-500 border-amber-600 text-white",
    desc: "AI performs the core task. Humans handle exceptions.",
  },
  {
    key: "augment",
    label: "Augment",
    icon: Brain,
    color: "bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100",
    activeColor: "bg-violet-500 border-violet-600 text-white",
    desc: "Human-led with AI as a force multiplier.",
  },
  {
    key: "protect",
    label: "Protect",
    icon: Shield,
    color: "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100",
    activeColor: "bg-emerald-500 border-emerald-600 text-white",
    desc: "Fundamentally human. AI would diminish value.",
  },
];

export const DecisionPanel = ({ stall, departmentName, boardId, isLocked, industry, companySize, onUpdate, onClose }) => {
  const [decision, setDecision] = useState(stall?.decision || null);
  const [confidence, setConfidence] = useState(stall?.confidence || 0);
  const [notes, setNotes] = useState(stall?.notes || "");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  const saveDecision = useCallback(async (newDecision) => {
    if (isLocked) {
      toast.error("Board is locked by facilitator");
      return;
    }
    setSaving(true);
    try {
      await stallAPI.updateDecision(boardId, stall.id, {
        decision: newDecision || "null",
        confidence,
        notes,
      });
      onUpdate({ ...stall, decision: newDecision, confidence, notes });
      toast.success("Decision saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [boardId, stall, confidence, notes, isLocked, onUpdate]);

  const handleDecisionClick = (key) => {
    const newVal = decision === key ? null : key;
    setDecision(newVal);
    saveDecision(newVal);
  };

  const saveNotes = async () => {
    if (isLocked) return;
    setSaving(true);
    try {
      await stallAPI.updateDecision(boardId, stall.id, {
        decision: decision || "null",
        confidence,
        notes,
      });
      onUpdate({ ...stall, decision, confidence, notes });
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const getAiSuggestion = async () => {
    setAiLoading(true);
    try {
      const res = await aiAPI.suggest({
        stall_name: stall.name,
        stall_description: stall.description,
        department_name: departmentName,
        industry: industry || "",
        company_size: companySize || "",
      });
      setAiSuggestion(res.data);
    } catch {
      toast.error("AI suggestion unavailable");
    } finally {
      setAiLoading(false);
    }
  };

  if (!stall) return null;

  return (
    <Sheet open={!!stall} onOpenChange={() => onClose()}>
      <SheetContent className="w-full sm:max-w-[440px] p-0 bg-[#F8FAFC] overflow-y-auto" data-testid="decision-panel">
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 py-4">
          <SheetHeader className="text-left">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs text-slate-500 border-slate-200 font-medium">
                {departmentName}
              </Badge>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <SheetTitle className="font-heading text-xl text-slate-900 mt-2 pr-6">
              {stall.name}
            </SheetTitle>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{stall.description}</p>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">
              Decision
            </label>
            <div className="space-y-2">
              {DECISIONS.map((d) => (
                <button
                  key={d.key}
                  data-testid={`decision-${d.key}-btn`}
                  onClick={() => handleDecisionClick(d.key)}
                  disabled={isLocked || saving}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 font-medium text-sm transition-colors ${
                    decision === d.key ? d.activeColor : d.color
                  } ${isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <d.icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-heading font-semibold">{d.label}</div>
                    <div className={`text-xs mt-0.5 ${decision === d.key ? "opacity-80" : "opacity-60"}`}>
                      {d.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">
              Confidence Level
            </label>
            <TooltipProvider>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((level) => (
                  <Tooltip key={level}>
                    <TooltipTrigger asChild>
                      <button
                        data-testid={`confidence-star-${level}`}
                        onClick={() => {
                          setConfidence(level === confidence ? 0 : level);
                        }}
                        className="p-1 hover:scale-110 active:scale-95"
                        disabled={isLocked}
                      >
                        <Star
                          className={`w-6 h-6 ${
                            level <= confidence
                              ? "fill-amber-400 text-amber-400"
                              : "text-slate-300"
                          }`}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {["Very Low", "Low", "Medium", "High", "Very High"][level - 1]}
                    </TooltipContent>
                  </Tooltip>
                ))}
                {confidence > 0 && (
                  <span className="text-xs text-slate-400 ml-2">{confidence}/5</span>
                )}
              </div>
            </TooltipProvider>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">
              Notes & Rationale
            </label>
            <Textarea
              data-testid="decision-notes-textarea"
              placeholder="Why did you make this decision? What context is important?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isLocked}
              className="min-h-[100px] bg-white border-slate-200 resize-none text-sm"
            />
            <Button
              variant="outline"
              size="sm"
              data-testid="save-notes-btn"
              onClick={saveNotes}
              disabled={isLocked || saving}
              className="mt-2 text-xs"
            >
              {saving ? "Saving..." : "Save Notes"}
            </Button>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                AI Suggestion
              </label>
              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">
                GPT-5.2
              </Badge>
            </div>
            {aiSuggestion ? (
              <div className="bg-white rounded-xl border border-slate-200 p-4 animate-fade-in">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                  <span className="font-heading font-semibold text-sm text-slate-900 capitalize">
                    {aiSuggestion.suggestion}
                  </span>
                  <span className="text-xs text-slate-400">
                    (Confidence: {aiSuggestion.confidence}/5)
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{aiSuggestion.reasoning}</p>
                {aiSuggestion.examples?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {aiSuggestion.examples.map((ex, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-500">
                        <span className="text-indigo-400 mt-0.5">-</span>
                        <span>{ex}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-3 italic">
                  AI suggestion — your context matters most
                </p>
              </div>
            ) : (
              <Button
                variant="outline"
                data-testid="ai-suggest-btn"
                onClick={getAiSuggestion}
                disabled={aiLoading}
                className="w-full border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-indigo-300"
              >
                {aiLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2 text-indigo-500" />Get AI Suggestion</>
                )}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DecisionPanel;
